package com.zi.support.shared

/**
 * One `supportDevices` record, reduced to what More > Devices and More > Apps show. [lastSeenMillis] is its updatedAt
 * (0 = never); [lastOpenedMillis] is lastOpenedAt, written when the app comes to the foreground; [appVersion] is like "1.8 (11)".
 */
data class SupportDevice(
    val deviceId: String,
    val platform: String,
    val model: String,
    val owner: String,
    val lastSeenMillis: Long,
    val appVersion: String = "",
    val lastOpenedMillis: Long = 0,
    /** The company server the app is connected to (Driver Hub and Operations App), e.g. "https://gantrans.net.za:56308". */
    val server: String = "",
    /** Every field of the device record, for apps whose phones live in NotificationCentre (More > Apps shows them all). */
    val details: List<DeviceDetail> = emptyList(),
    /** NotificationCentre's number for this installation, e.g. "OPS-004"; "" until the phone writes again after the registry was added. */
    val number: String = "",
    /** False once the app logged out (NotificationCentre apps); the phone still shows. */
    val active: Boolean = true,
    /** Newest first. */
    val history: List<DeviceHistoryEntry> = emptyList(),
)

data class ServerGroup(val label: String, val devices: List<SupportDevice>)

data class DeviceDetail(val label: String, val value: String)

data class DeviceHistoryEntry(val atMillis: Long, val text: String)

/**
 * Online/offline for More > Devices on web, Android and iOS. Uses the same rule as the heartbeat sent to the
 * Central Services API (functions/lib/device-heartbeats.js), so the app and that dashboard agree.
 */
object DeviceStatus {
    /** The apps check in every 15 minutes; one late tick is allowed before a device counts as offline. */
    const val ONLINE_WINDOW_MILLIS: Long = 20L * 60 * 1000

    fun isOnline(device: SupportDevice, nowMillis: Long): Boolean =
        device.lastSeenMillis > 0 && nowMillis - device.lastSeenMillis <= ONLINE_WINDOW_MILLIS

    /** Online first, then by device number. */
    fun sorted(devices: List<SupportDevice>, nowMillis: Long): List<SupportDevice> =
        devices.sortedWith(compareBy<SupportDevice> { !isOnline(it, nowMillis) }.thenBy { it.deviceId.ifBlank { "~" } })

    fun lastSeenLabel(device: SupportDevice, nowMillis: Long): String {
        if (device.lastSeenMillis <= 0) return "Never seen"
        return "Seen ${ago(device.lastSeenMillis, nowMillis)}"
    }

    internal fun ago(millis: Long, nowMillis: Long): String {
        val minutes = (nowMillis - millis).coerceAtLeast(0) / 60_000
        return when {
            minutes < 1 -> "just now"
            minutes < 60 -> "$minutes min ago"
            minutes < 48 * 60 -> "${minutes / 60} h ago"
            else -> "${minutes / (24 * 60)} days ago"
        }
    }

    /** "CPH2669 · android · keyur", plus the server when the app reports one: "· gantrans.net.za:56308". */
    fun subtitle(device: SupportDevice): String =
        listOf(device.model, device.platform, device.owner.substringBefore('@'), serverLabel(device.server))
            .filter { it.isNotBlank() }.joinToString(" · ")

    /** "https://vmi3493472.tailc4ba6c.ts.net:56310/" → "vmi3493472:56310"; "https://gantrans.net.za:56308" → "gantrans.net.za:56308". */
    fun serverLabel(url: String): String {
        val hostPort = url.substringAfter("://").substringBefore('/')
        val host = hostPort.substringBefore(':')
        return if (host.endsWith(".ts.net")) host.substringBefore('.') + hostPort.removePrefix(host) else hostPort
    }
}

/** More > Apps on web, Android and iOS: each app's devices, when the app was last opened there, and which version it runs. */
object AppInstalls {
    const val SUPPORT_APP = "Support App"
    const val DRIVER_HUB = "Driver Hub"
    const val OPERATIONS_APP = "Operations App"
    const val HEATING_APP = "Heating App"
    const val CARGOVIEW = "CargoView"
    const val ATTENDANCE_APP = "Attendance App"

    /** The tabs. */
    val apps: List<String> = listOf(SUPPORT_APP, DRIVER_HUB, OPERATIONS_APP, HEATING_APP, ATTENDANCE_APP, CARGOVIEW)

    /**
     * Operations, heating and CargoView phones register in NotificationCentre, which the Support App can't read directly:
     * call the listAppDevices function with {app: this key} instead of listening. Null = a collection in this project.
     */
    fun notificationCentreKey(app: String): String? = when (app) {
        OPERATIONS_APP -> "operations"
        HEATING_APP -> "heating"
        CARGOVIEW -> "cargoview"
        else -> null
    }

    /** Where each app keeps one record per phone in this project. Query with `active == true` when [activeOnly] says so. */
    fun collectionPaths(app: String): List<String> = when (app) {
        DRIVER_HUB -> listOf("driverHubDevices")
        SUPPORT_APP -> listOf("supportDevices")
        ATTENDANCE_APP -> listOf("attendanceDevices")
        else -> emptyList()
    }

    /** Support App records turn inactive on logout or when replaced, so only active ones are current. Driver Hub has one per phone. */
    fun activeOnly(app: String): Boolean = app == SUPPORT_APP

    /** Credentials, never shown in a device's fields. */
    val HIDDEN_FIELDS: Set<String> = setOf("token", "secretHash", "accountKey", "fcmToken", "pushToken", "ownerEmail")

    /**
     * One record from [collectionPaths]; [text] reads a string field ("" when missing), [millis] a timestamp (0 when missing),
     * [fields] is every field as text for the device popup (credentials are dropped here).
     */
    fun device(app: String, path: String, text: (String) -> String, millis: (String) -> Long, fields: Map<String, String>): SupportDevice {
        val (name, owner) = when (app) {
            DRIVER_HUB -> text("driverName").ifBlank { "Not logged in" } to text("companyName")
            ATTENDANCE_APP -> text("operatorName").ifBlank { "Not logged in" } to text("siteName")
            else -> text("deviceId") to SupportLogin.label(text("ownerEmail"))
        }
        val details = fields.filterKeys { it !in HIDDEN_FIELDS }.entries.sortedBy { it.key }.map { DeviceDetail(it.key, it.value) }
        return SupportDevice(name, text("platform"), text("model"), owner, millis("updatedAt"), text("appVersion"), millis("lastOpenedAt"),
            // The Support App's number is already its deviceId; the others show theirs (20000…, 50000…) beside the person.
            if (app == SUPPORT_APP) "" else text("serverUrl"), details, number = if (app == SUPPORT_APP) "" else text("deviceId"))
    }

    /**
     * One row from listAppDevices: [path] like "operations-app/kph/devices/{id}" or
     * "customer-app/texas/subcompanies/investec/customerAppDevices/{id}", [fields] every field as text.
     */
    fun centreDevice(
        app: String, path: String, fields: Map<String, String>, updatedMillis: Long, openedMillis: Long,
        number: String, active: Boolean, history: List<DeviceHistoryEntry>,
    ): SupportDevice {
        fun text(key: String) = fields[key].orEmpty()
        val parts = path.split('/')
        val company = parts.getOrElse(1) { "" }
        val name = when (app) {
            OPERATIONS_APP -> text("fullName").ifBlank { text("accountName") }
            HEATING_APP -> text("operator")
            else -> parts.getOrElse(3) { "" }
        }.ifBlank { "Not logged in" }
        val details = listOf(DeviceDetail("path", path)) + fields.filterKeys { it !in HIDDEN_FIELDS }.entries.sortedBy { it.key }.map { DeviceDetail(it.key, it.value) }
        return SupportDevice(name, text("platform"), text("model"), company, updatedMillis, text("appVersion"), openedMillis,
            text("serverUrl").ifBlank { text("serverKey") }, details, number, active, history)
    }

    const val NOT_CONFIGURED = "Not configured"

    /** Every app but the Support App and the Attendance App connects to a company server, so More > Apps lists its servers before its devices. */
    fun hasServers(app: String): Boolean = app != SUPPORT_APP && app != ATTENDANCE_APP

    /** One group per server, e.g. "gantrans.net.za:56308" or "vmi3493472:56310", sorted; devices without one under "Not configured", last. */
    fun servers(devices: List<SupportDevice>): List<ServerGroup> =
        devices.groupBy { serverOf(it) }.entries.sortedWith(compareBy({ it.key == NOT_CONFIGURED }, { it.key })).map { ServerGroup(it.key, it.value) }

    fun serverOf(device: SupportDevice): String = DeviceStatus.serverLabel(device.server).lowercase().ifBlank { NOT_CONFIGURED }

    /** "Logged in" / "Logged out" for NotificationCentre apps. */
    fun stateLabel(device: SupportDevice): String = if (device.active) "Logged in" else "Logged out"

    /** Most recently opened first. */
    fun sorted(devices: List<SupportDevice>): List<SupportDevice> =
        devices.sortedWith(compareByDescending<SupportDevice> { it.lastOpenedMillis }.thenBy { it.deviceId.ifBlank { "~" } })

    /**
     * How recently an app was used, as a colour (More > Apps and Locations): opened (or, on the map, last located) within
     * the last hour green, 1 to 2 hours orange, longer yellow; never: none.
     */
    enum class Freshness(val hex: String) { GREEN("#16a34a"), ORANGE("#ea580c"), YELLOW("#ca8a04"), NONE("#94a3b8") }

    fun freshness(lastMillis: Long, nowMillis: Long): Freshness = when {
        lastMillis <= 0 -> Freshness.NONE
        nowMillis - lastMillis < 60 * 60_000L -> Freshness.GREEN
        nowMillis - lastMillis < 2 * 60 * 60_000L -> Freshness.ORANGE
        else -> Freshness.YELLOW
    }

    fun openedLabel(device: SupportDevice, nowMillis: Long): String =
        if (device.lastOpenedMillis <= 0) "Not opened since this was added" else "Opened ${DeviceStatus.ago(device.lastOpenedMillis, nowMillis)}"

    fun versionLabel(device: SupportDevice): String = device.appVersion.ifBlank { "Unknown version" }

    /** True when any other device runs a newer version. Android and iOS share one version number per app (version.xcconfig / version.properties). */
    fun isOutdated(device: SupportDevice, devices: List<SupportDevice>): Boolean {
        if (device.appVersion.isBlank()) return false
        val newest = devices.filter { it.appVersion.isNotBlank() }
            .maxWithOrNull { a, b -> compareVersions(a.appVersion, b.appVersion) } ?: return false
        return compareVersions(device.appVersion, newest.appVersion) < 0
    }

    /** "1.8 (11)" vs "1.10 (12)": compares the numbers in order, so 1.10 is newer than 1.8. */
    fun compareVersions(a: String, b: String): Int {
        val x = Regex("\\d+").findAll(a).map { it.value.toLong() }.toList()
        val y = Regex("\\d+").findAll(b).map { it.value.toLong() }.toList()
        for (i in 0 until maxOf(x.size, y.size)) {
            val c = (x.getOrElse(i) { 0 }).compareTo(y.getOrElse(i) { 0 })
            if (c != 0) return c
        }
        return 0
    }
}
